import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, X } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WEEKDAYS, title, fmtDate } from "../lib/format";
import PasswordForm from "../components/PasswordForm";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLE = { staff: "Sales Staff", manager: "Store Manager", admin: "Admin" };

const Detail = ({ label, value, testid, children }) => (
  <div className="px-5 py-4" data-testid={testid}>
    <div className="bs-eyebrow">{label}</div>
    {children || <div className={`text-lg font-semibold mt-1 break-words ${value ? "text-[#011E38]" : "text-slate-400"}`} data-testid={`${testid}-value`}>{value || "Not set"}</div>}
  </div>
);

function MobileField({ p, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(p.mobile || "");
  const save = async () => {
    try { await api.put("/me/mobile", { mobile: v }); toast.success("Mobile number updated"); setEdit(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!edit)
    return (
      <div className="flex items-center justify-between mt-1">
        <div className={`num text-lg font-semibold ${p.mobile ? "text-[#011E38]" : "text-slate-400"}`} data-testid="detail-mobile-value">{p.mobile || "Not set"}</div>
        <button onClick={() => { setV(p.mobile || ""); setEdit(true); }} className="text-slate-400 hover:text-[#011E38]" data-testid="edit-mobile-button"><Pencil className="w-4 h-4" /></button>
      </div>
    );
  return (
    <div className="flex gap-2 mt-1.5">
      <Input type="tel" inputMode="numeric" maxLength={10} value={v} onChange={(e) => setV(e.target.value.replace(/\D/g, ""))} className="h-11 text-base num" data-testid="mobile-input" />
      <Button size="icon" className="h-11 w-11 bg-[#011E38]" onClick={save} data-testid="save-mobile-button"><Check className="w-4 h-4" /></Button>
      <Button size="icon" variant="outline" className="h-11 w-11" onClick={() => setEdit(false)} data-testid="cancel-mobile-button"><X className="w-4 h-4" /></Button>
    </div>
  );
}

function MyDetails() {
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get("/me/profile").then((r) => setP(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  return (
    <section data-testid="my-details">
      <div className="bs-eyebrow mb-2">My Details</div>
      <div className="bs-card divide-y divide-slate-100">
        <Detail label="Name" value={p.name} testid="detail-name" />
        <Detail label="Employee ID" value={p.employee_id} testid="detail-employee-id" />
        <Detail label="BlueStone Email" value={p.email} testid="detail-email" />
        <Detail label="Mobile Number" testid="detail-mobile"><MobileField p={p} onSaved={load} /></Detail>
        <div className="grid grid-cols-2 divide-x divide-slate-100">
          <Detail label="Age in BlueStone" value={p.tenure} testid="detail-tenure" />
          <Detail label="Date of Joining" value={p.date_of_joining && fmtDate(p.date_of_joining)} testid="detail-doj" />
        </div>
        {p.role !== "admin" && p.role !== "manager" && <Detail label="Current Store" value={p.store_name} testid="detail-store" />}
        <div className="grid grid-cols-2 divide-x divide-slate-100">
          <Detail label="Role" value={ROLE[p.role]} testid="detail-role" />
          <Detail label="Account Status" value={p.active ? "Active" : "Disabled"} testid="detail-status" />
        </div>
        {p.role === "staff" && <Detail label="Reporting Manager" value={p.managers.join(", ")} testid="detail-manager" />}
      </div>
      <p className="text-xs text-slate-500 mt-2">To correct other details, contact your store manager.</p>
    </section>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [off, setOff] = useState(user.weekly_off == null ? "none" : String(user.weekly_off));

  const saveOff = async (v) => {
    setOff(v);
    try {
      await api.put("/me/weekly-off", { weekly_off: v === "none" ? null : Number(v) });
      toast.success("Weekly off updated");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6 max-w-xl bs-rise">
      <div>
        <div className="bs-eyebrow">Profile</div>
        <h1 className="font-display text-4xl font-extrabold text-[#011E38] tracking-tight mt-1" data-testid="account-title">{user.name}</h1>
      </div>
      <MyDetails />
      {user.role === "staff" && (
        <div className="bs-card p-6">
          <div className="bs-eyebrow mb-2">My weekly off</div>
          <Select value={off} onValueChange={saveOff}>
            <SelectTrigger className="h-12 text-base" data-testid="weekly-off-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No weekly off</SelectItem>
              {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-2">Your weekly off is excluded from your working days and expected target.</p>
        </div>
      )}
      <div className="bs-card p-6">
        <div className="bs-eyebrow mb-4">Change password</div>
        <PasswordForm />
      </div>
    </div>
  );
}
